/**
 * AuthGate — exhaustive handler for all DXCUserInteraction types.
 *
 * This is the core auth safety component. It observes db.cloud.userInteraction
 * and routes each interaction type to the correct component:
 *
 *   "email"               → SignIn (email input + OAuth buttons)
 *   "otp"                 → SignIn (OTP input, auto-submit at 8 chars)
 *   "message-alert"       → MessageAlert (errors, license limits)
 *   "logout-confirmation" → LogoutConfirmation (Cancel = DEFAULT)
 *   "generic"             → GenericInteraction (dynamic fields)
 *
 * NEVER-17: logout-confirmation must NEVER lead to silent data deletion.
 *           Cancel is the default/prominent action.
 * NEVER-08: Intended destination is preserved through auth flow.
 * NEVER-09: Loading state shown during db.open(), NEVER empty state.
 *
 * This replaces the old DatabaseProvider auth gate which only handled
 * email + otp types.
 */

import { type DXCUserInteraction } from "dexie-cloud-addon"
import { SignIn } from "./SignIn"
import { MessageAlert } from "./MessageAlert"
import { LogoutConfirmation } from "./LogoutConfirmation"
import { GenericInteraction } from "./GenericInteraction"

interface AuthGateInteractionProps {
  interaction: DXCUserInteraction
}

/**
 * Routes a DXCUserInteraction to the correct component.
 * Exhaustive switch ensures every type is handled — TypeScript
 * will catch missing cases at compile time.
 */
export function AuthGateInteraction({ interaction }: AuthGateInteractionProps) {
  const { type, fields, onSubmit, onCancel, cancelLabel, submitLabel, alerts } =
    interaction

  switch (type) {
    case "email":
    case "otp":
      return (
        <SignIn
          fields={fields}
          onSubmit={onSubmit}
          onCancel={onCancel}
          type={type}
          alerts={alerts}
        />
      )

    case "message-alert":
      return (
        <MessageAlert
          alerts={alerts}
          onSubmit={onSubmit}
          onCancel={onCancel}
          submitLabel={submitLabel}
          cancelLabel={cancelLabel}
        />
      )

    case "logout-confirmation":
      // ⚠️ CRITICAL — Cancel MUST be default. See NEVER-17.
      return (
        <LogoutConfirmation
          alerts={alerts}
          onSubmit={onSubmit}
          onCancel={onCancel}
          submitLabel={submitLabel}
          cancelLabel={cancelLabel}
        />
      )

    case "generic":
      return (
        <GenericInteraction
          fields={fields}
          onSubmit={onSubmit}
          onCancel={onCancel}
          alerts={alerts}
          submitLabel={submitLabel}
          cancelLabel={cancelLabel}
        />
      )

    default: {
      // Exhaustive check — TypeScript will error if a new type is added
      // to DXCUserInteraction but not handled here
      const _exhaustive: never = type
      console.error(
        `[AuthGate] Unknown DXCUserInteraction type: ${_exhaustive}`,
      )
      // Fallback to generic
      return (
        <GenericInteraction
          fields={fields}
          onSubmit={onSubmit}
          onCancel={onCancel}
          alerts={alerts}
          submitLabel={submitLabel}
          cancelLabel={cancelLabel}
        />
      )
    }
  }
}
